import './instrument';

import React from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import App from './App';
import { configureStore } from '@reduxjs/toolkit';
import appReducer from './appState/stateReducer';
import './styles/themes.css';
import ThemeInitializer from './theme/ThemeInitializer';
import {
  ErrorProvider,
  GlobalDataProvider,
  GlobalActionProvider,
  SessionDataProvider,
  SessionActionProvider,
  UserActionProvider,
  UnsavedChangesProvider,
  GatewayProvider,
} from './providers';
import * as Sentry from '@sentry/react';

const store = configureStore({
  reducer: appReducer,
});

const queryClient = new QueryClient();

export type AppState = ReturnType<typeof store.getState>;

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement,
  {
    onUncaughtError: Sentry.reactErrorHandler(),
    onCaughtError: Sentry.reactErrorHandler(),
    onRecoverableError: Sentry.reactErrorHandler(),
  },
);

root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <Provider store={store}>
        <ErrorProvider>
          <GlobalDataProvider>
            <GlobalActionProvider>
              <SessionDataProvider>
                <SessionActionProvider>
                  <UserActionProvider>
                    <GatewayProvider>
                      <UnsavedChangesProvider>
                        <ThemeInitializer />
                        <Sentry.ErrorBoundary
                          fallback={
                            <p>
                              Something went wrong. Please refresh the page and
                              try again.
                            </p>
                          }
                        >
                          <App />
                        </Sentry.ErrorBoundary>
                      </UnsavedChangesProvider>
                    </GatewayProvider>
                  </UserActionProvider>
                </SessionActionProvider>
              </SessionDataProvider>
            </GlobalActionProvider>
          </GlobalDataProvider>
        </ErrorProvider>
      </Provider>
    </QueryClientProvider>
  </React.StrictMode>,
);
